import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../layout/Card.jsx';
import { Icon } from '../../layout/Icon.jsx';
import { api, apiError } from '../../shared/api.js';
import { toast } from '../../shared/useToast.js';
import { useCompanionEntryFee } from '../../shared/useSettings.js';
import { Stepper } from './components/Stepper.jsx';
import { ReceiptPreview } from './components/ReceiptPreview.jsx';
import { Step1Guardian } from './steps/Step1Guardian.jsx';
import { Step2Child } from './steps/Step2Child.jsx';
import { Step3Package } from './steps/Step3Package.jsx';
import { Step4CardPayment } from './steps/Step4CardPayment.jsx';
import { useReceptionForm } from './hooks/useReceptionForm.js';
import { useSubscriptionCoverage } from './hooks/useSubscriptionCoverage.js';

// حدود خطأ محلية تمنع اختفاء واجهة الاستقبال إذا حدث خطأ في خطوة من الخطوات
// (مثلاً: فشل تشغيل الكاميرا، خطأ شبكة في البحث عن ولي الأمر، إلخ).
class ReceptionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Reception error:', error, info);
  }
  reset = () => this.setState({ error: null });
  render() {
    if (this.state.error) {
      return (
        <div className="card p-6 text-center max-w-lg mx-auto mt-8">
          <div className="text-[15px] font-bold text-red mb-2">حدث خطأ في شاشة الاستقبال</div>
          <div className="text-ink-2 text-[12px] mb-4">
            {String(this.state.error.message || this.state.error)}
          </div>
          <div className="flex gap-2 justify-center">
            <button className="btn btn-primary" onClick={this.reset}>إعادة المحاولة</button>
            <button className="btn" onClick={() => window.location.reload()}>إعادة تحميل الصفحة</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ReceptionInner() {
  const navigate = useNavigate();
  const step = useReceptionForm((s) => s.step);
  const next = useReceptionForm((s) => s.next);
  const prev = useReceptionForm((s) => s.prev);
  const guardian = useReceptionForm((s) => s.guardian);
  const children = useReceptionForm((s) => s.children);
  const pricing = useReceptionForm((s) => s.pricing);
  const payment = useReceptionForm((s) => s.payment);
  const sharedCard = useReceptionForm((s) => s.sharedCard);
  const companionFee = useCompanionEntryFee();
  // المشتركون شهرياً: الخادم يفتح جلستهم بلا رسم لعب، فلا يُحتجز لهم نصيب من
  // المبلغ المستلم — وإلا بقي نصيب غيرهم ناقصاً وظهر عليهم مستحق وهمي.
  const coverage = useSubscriptionCoverage();
  const reset = useReceptionForm((s) => s.reset);
  const markAttempt = useReceptionForm((s) => s.markAttempt);
  const isStep1Valid = useReceptionForm((s) => s.isStep1Valid);
  const isStep2Valid = useReceptionForm((s) => s.isStep2Valid);
  const isStep3Valid = useReceptionForm((s) => s.isStep3Valid);
  const isStep4Valid = useReceptionForm((s) => s.isStep4Valid);

  const [submitting, setSubmitting] = useState(false);
  // تقدُّم الإرسال: إذا فشل الطلب في منتصف العائلة وأعاد الموظف المحاولة،
  // نتخطّى ما نجح فعلاً بدل إعادة إنشائه (كان يضاعف النقد في القاصة).
  const progressRef = useRef({ childIds: {}, sessionIds: {} });

  const canProceed =
    (step === 1 && isStep1Valid()) ||
    (step === 2 && isStep2Valid()) ||
    (step === 3 && isStep3Valid()) ||
    (step === 4 && isStep4Valid());

  async function handleSubmit() {
    setSubmitting(true);
    try {
      // 1. Ensure guardian exists. If id is already known (lookup hit during typing)
      //    use it directly. Otherwise do a fresh by-phone lookup so we never POST
      //    over an existing record (which would 409 on the unique phone index).
      let guardianId = guardian.id;
      if (!guardianId) {
        try {
          const { data: existing } = await api.get(`/guardians/by-phone/${guardian.phone}`);
          guardianId = existing?._id || existing?.id || null;
        } catch (lookupErr) {
          if (lookupErr?.response?.status !== 404) throw lookupErr;
          // 404 → really new — fall through to POST.
        }
      }
      if (!guardianId) {
        const { data: created } = await api.post('/guardians', {
          name: guardian.name,
          phone: guardian.phone,
          secondaryPhone: guardian.secondaryPhone || undefined,
          relation: guardian.relation,
          notes: guardian.notes || undefined
        });
        guardianId = created?.guardian?._id || created?._id || created?.data?._id;
      }

      // 2. Ensure children exist. Previously registered children (imported from
      //    the phone lookup) carry childId — they are reused as-is, with an
      //    optional PATCH if the receptionist edited their data. Only brand-new
      //    children are POSTed (multipart with photo).
      const existingById = new Map(
        useReceptionForm
          .getState()
          .existingChildren.map((x) => [String(x._id || x.id), x])
      );
      const created = [];
      for (const c of children) {
        if (c.childId) {
          const snap = existingById.get(String(c.childId));
          const patch = {};
          if (snap) {
            const num = (v) => (String(v ?? '').trim() === '' ? null : Number(v));
            if ((c.name || '').trim().length >= 2 && c.name.trim() !== snap.name) patch.name = c.name.trim();
            if (num(c.age) != null && num(c.age) !== snap.age) patch.age = num(c.age);
            if (c.gender && c.gender !== snap.gender) patch.gender = c.gender;
            if (num(c.height) != null && num(c.height) !== (snap.height ?? null)) patch.height = num(c.height);
            if (num(c.weight) != null && num(c.weight) !== (snap.weight ?? null)) patch.weight = num(c.weight);
            if ((c.medicalNotes || '').trim() !== (snap.medicalNotes || '')) patch.medicalNotes = (c.medicalNotes || '').trim();
            if ((c.allergyNotes || '').trim() !== (snap.allergyNotes || '')) patch.allergyNotes = (c.allergyNotes || '').trim();
          }
          // فشل تحديث البيانات أو الصورة لا يمنع فتح الجلسة،
          // وفشل أحدهما لا يُسقط الآخر.
          if (Object.keys(patch).length) {
            try {
              await api.patch(`/children/${c.childId}`, patch);
            } catch (updateErr) {
              toast.warning(`تعذّر تحديث بيانات ${c.name}: ${apiError(updateErr)}`);
            }
          }
          if (c.photoBlob) {
            try {
              const pfd = new FormData();
              pfd.append('photo', c.photoBlob, `${c.name || 'child'}.jpg`);
              await api.post(`/children/${c.childId}/photo`, pfd, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
            } catch (photoErr) {
              toast.warning(`تعذّر تحديث صورة ${c.name}: ${apiError(photoErr)}`);
            }
          }
          created.push({ ...c });
          continue;
        }

        const fd = new FormData();
        fd.append('guardianId', guardianId);
        fd.append('name', c.name);
        fd.append('age', String(c.age));
        fd.append('gender', c.gender);
        if (c.height) fd.append('height', String(c.height));
        if (c.weight) fd.append('weight', String(c.weight));
        if (c.medicalNotes) fd.append('medicalNotes', c.medicalNotes);
        if (c.allergyNotes) fd.append('allergyNotes', c.allergyNotes);
        if (c.cardUid && String(c.cardUid).trim()) fd.append('cardUid', String(c.cardUid).trim().toUpperCase());
        if (c.photoBlob) fd.append('photo', c.photoBlob, `${c.name || 'child'}.jpg`);
        const { data } = await api.post('/children', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const childId = data?.child?._id || data?._id || data?.data?._id;
        // يُحفظ في النموذج فوراً: لو فشل فتح الجلسات وأعاد الموظف المحاولة،
        // لا يُنشأ سجل طفل مكرر بل يُعاد استخدام هذا المعرف.
        if (childId) useReceptionForm.getState().updateChild(c.tempId, { childId });
        created.push({ ...c, childId });
      }

      // 3. Open sessions
      // المبلغ المستلم واحد لكل العائلة، لكن كل طفل له فاتورة دخول مستقلة.
      // نوزّع المبلغ على الأطفال حسب ما هو متوقَّع من كل واحد، فلا يُختم
      // المبلغ كاملاً على كل طفل (دفع مضاعف) ولا يبقى الباقون بلا سداد.
      // فشل جلسة طفل لا يمنع فتح جلسات البقية.
      const sharedUid = sharedCard.enabled
        ? String(sharedCard.uid || '').trim().toUpperCase()
        : null;
      const companionsCount = Math.max(0, Math.round(Number(sharedCard.companionsCount) || 0));

      // رسم اللعب للطفل الواحد — الساعي يُحتسب عند الخروج فلا يُدفع عند الدخول.
      const playPerChild = pricing.type === 'package'
        ? Number(pricing.package?.price) || 0
        : pricing.type === 'fixed'
          ? Number(pricing.fixedAmount) || 0
          : pricing.type === 'open'
            ? Number(pricing.openAmount) || 0
            : 0;

      const companionsAmount = companionsCount * companionFee;
      let tenderLeft = Math.max(0, Math.round(Number(payment.paidAmount) || 0));

      const sessionIds = [];
      const sessionFailures = [];
      for (let i = 0; i < created.length; i += 1) {
        const c = created[i];
        // ما هو متوقَّع من هذا الطفل: رسم اللعب (صفر في الساعي) + المرافقون
        // على الطفل الأول فقط. الخادم يعيد تقييده بإجمالي الفاتورة الفعلي.
        const covered = coverage.isCovered(c);
        const expected = (covered ? 0 : playPerChild) + (i === 0 ? companionsAmount : 0);
        const share = Math.max(0, Math.min(expected, tenderLeft));
        tenderLeft -= share;

        // جلسة فُتحت في محاولة سابقة — لا تُفتح ثانيةً ولا يُخصم المبلغ مرتين.
        const done = progressRef.current.sessionIds[c.tempId];
        if (done) { sessionIds.push(done); continue; }

        const body = {
          childId: c.childId,
          guardianId,
          cardUid: sharedUid || (c.cardUid ? c.cardUid : undefined),
          allowSharedCard: !!sharedUid,
          // المرافقون يُسجَّلون مرة واحدة على أول طفل في المجموعة.
          companionsCount: i === 0 ? companionsCount : 0,
          pricingType: pricing.type,
          ruleId: pricing.type === 'hourly' ? pricing.ruleId : undefined,
          packageId: pricing.type === 'package' ? pricing.packageId : undefined,
          fixedAmount: pricing.type === 'fixed' ? Number(pricing.fixedAmount) : undefined,
          openAmount: pricing.type === 'open' ? Number(pricing.openAmount) : undefined,
          paymentMethod: payment.method,
          paidAmount: share,
          cashboxId: payment.cashboxId || undefined
        };
        try {
          const { data } = await api.post('/sessions', body);
          const sid = data?.session?._id || data?._id || data?.data?._id;
          if (sid) {
            progressRef.current.sessionIds[c.tempId] = sid;
            sessionIds.push(sid);
          }
        } catch (sessionErr) {
          sessionFailures.push({ name: c.name, message: apiError(sessionErr) });
        }
      }

      for (const f of sessionFailures) {
        toast.error(`${f.name}: ${f.message}`);
      }
      if (sessionIds.length === 0) {
        // لم تُفتح أي جلسة — يبقى النموذج كما هو ليعالج الموظف السبب.
        return;
      }
      toast.success(
        sharedUid
          ? `تم فتح ${sessionIds.length} جلسة على الكارت ${sharedUid}`
          : `تم فتح ${sessionIds.length} جلسة بنجاح`
      );
      progressRef.current = { childIds: {}, sessionIds: {} };
      reset();
      navigate('/active');
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <Stepper step={step} />
      <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 360px' }}>
        <div className="card">
          {step === 1 ? <Step1Guardian /> : null}
          {step === 2 ? <Step2Child /> : null}
          {step === 3 ? <Step3Package /> : null}
          {step === 4 ? <Step4CardPayment /> : null}

          <div className="px-[18px] py-3.5 border-t border-line flex items-center justify-between gap-3">
            <button
              type="button"
              className="btn"
              onClick={prev}
              disabled={step === 1 || submitting}
            >
              <Icon name="chevronR" size={14} /> السابق
            </button>
            {step < 4 ? (
              // الزر يظل قابلاً للضغط حتى لو كان النموذج ناقصاً، ليُظهر للمستخدم
              // الحقول المطلوبة باللون الأحمر (markAttempt) بدلاً من تجمّد صامت.
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  if (!canProceed) {
                    markAttempt();
                    toast.warning('الرجاء تعبئة الحقول المطلوبة');
                    return;
                  }
                  next();
                }}
                disabled={submitting}
                style={!canProceed ? { opacity: 0.6 } : undefined}
              >
                التالي <Icon name="chevronL" size={14} />
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary btn-lg"
                onClick={handleSubmit}
                disabled={!canProceed || submitting}
              >
                <Icon name="check" size={16} /> {submitting ? 'جاري الفتح...' : 'تأكيد وفتح جلسة'}
              </button>
            )}
          </div>
        </div>

        <Card header="معاينة الإيصال" sub="طابعة 80mm">
          <ReceiptPreview />
        </Card>
      </div>
    </div>
  );
}

export default function Reception() {
  return (
    <ReceptionErrorBoundary>
      <ReceptionInner />
    </ReceptionErrorBoundary>
  );
}
